import { DatumValue } from "@nivo/core";
import { CustomLayerProps } from "@nivo/line";
import type { TicksSpec } from "@nivo/scales";
import { animated, config, useSpring } from "@react-spring/web";
import React, { useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import { FLEX } from "styles/flex";
import { calcRem } from "styles/theme";
import { convertBytesWithUnit } from "utils/functions";
import {
  CHART_MARGIN_BOTTOM,
  CHART_MARGIN_TOP,
  convertToShortFormat,
} from "utils/ChartUtil";

const LINE_DRAW_DURATION_MS = 900;

type MountOnceLinePathProps = {
  serieKey: string;
  path: string;
  color: string;
  strokeWidth: number;
  drawnIds: React.MutableRefObject<Set<string>>;
};

/** 마운트(또는 데이터 변경) 시 1회만 그리기 — hover 리렌더 시에는 정적 path */
function MountOnceLinePath({
  serieKey,
  path,
  color,
  strokeWidth,
  drawnIds,
}: MountOnceLinePathProps) {
  const measureRef = useRef<SVGPathElement>(null);
  const [pathLength, setPathLength] = useState(0);
  const alreadyDrawn = drawnIds.current.has(serieKey);

  useLayoutEffect(() => {
    const length = measureRef.current?.getTotalLength() ?? 0;
    setPathLength(length);
  }, [path]);

  const { dashoffset } = useSpring({
    from: { dashoffset: pathLength },
    to: { dashoffset: 0 },
    immediate: alreadyDrawn || pathLength <= 0,
    config: { ...config.slow, duration: LINE_DRAW_DURATION_MS },
    onRest: () => {
      drawnIds.current.add(serieKey);
    },
  });

  if (alreadyDrawn) {
    return (
      <path
        d={path}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    );
  }

  return (
    <>
      <path
        ref={measureRef}
        d={path}
        fill="none"
        stroke="none"
        visibility="hidden"
      />
      {pathLength > 0 && (
        <animated.path
          d={path}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={pathLength}
          strokeDashoffset={dashoffset}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
    </>
  );
}

function OffsetLinesLayerInner({
  strokeWidth,
  series,
  lineGenerator,
  xScale,
  yScale,
}: CustomLayerProps & { strokeWidth: number }) {
  const offsetX = 2;
  const drawnIds = useRef(new Set<string>());
  const seriesSignatureRef = useRef("");

  const seriesSignature = series?.map((s) => s.id).join(",") ?? "";

  useLayoutEffect(() => {
    if (seriesSignatureRef.current !== seriesSignature) {
      drawnIds.current.clear();
      seriesSignatureRef.current = seriesSignature;
    }
  }, [seriesSignature]);

  if (!lineGenerator || !series || series.length === 0) return null;

  return (
    <g>
      {series.map(({ id, data, color }) => {
        if (!data || data.length === 0) return null;

        const serieKey = String(id);
        const points = data.map((d: any) => {
          const x = xScale(d.data.x);
          const y = yScale(d.data.y);
          return {
            x: typeof x === "number" ? x + offsetX : 0,
            y: typeof y === "number" ? y : 0,
          };
        });

        try {
          const path = lineGenerator(points as any);
          if (!path) return null;

          return (
            <MountOnceLinePath
              key={serieKey}
              serieKey={serieKey}
              path={path}
              color={color ?? "#0284C7"}
              strokeWidth={strokeWidth}
              drawnIds={drawnIds}
            />
          );
        } catch {
          return null;
        }
      })}
    </g>
  );
}

/** Nivo custom layer — React 컴포넌트로 감싸 hover 시 state 유지 */
export const createOffsetLinesLayer = (strokeWidth: number) => {
  const Layer = (props: CustomLayerProps) => (
    <OffsetLinesLayerInner {...props} strokeWidth={strokeWidth} />
  );
  return Layer;
};

export default function GridYAxis({
  gridYValues,
  tickYValues,
  enableGridY,
  unit = "number",
}: {
  gridYValues?: TicksSpec<DatumValue>;
  tickYValues: DatumValue[];
  enableGridY?: boolean;
  unit: string;
}) {
  const yAxisTickValues =
    enableGridY && Array.isArray(gridYValues) ? gridYValues : tickYValues;
  return (
    <GraphYAxis>
      {[...yAxisTickValues].reverse().map((v) => (
        <span key={String(v)}>
          {unit === "bytes"
            ? convertBytesWithUnit(Number(v))
            : convertToShortFormat(Number(v))}
        </span>
      ))}
    </GraphYAxis>
  );
}

export const GraphYAxis = styled.div`
  ${FLEX.FlexBetweenEnd};
  flex-direction: column;
  padding: ${CHART_MARGIN_TOP}px 0 ${CHART_MARGIN_BOTTOM}px 0;
  font-size: ${calcRem(12)};
  color: ${({ theme }) => theme.COLORS?.WHITE}80;
  height: 100%;
  flex-shrink: 0;
  span {
    display: inline-block;
  }
`;
